import {
    CartWidthTypes,
    FontStylesTypes,
    HeaderHeightTypes,
    UpsellsLayoutType,
    ProductRecommendationType,
    ProductRecommendationIntent,
    AdditionalProductType,
    CartDrawerTemplatesTypes,
    AvailableCartDrawerTemplates,
    CustomHtmlPosTypes,
    CartDrawerFooterTypes,
    AvailableCartFooterLayouts,
} from "../Bundles/constants";
import { ProductObj } from "../Bundles/Types";
import { IS_METAL_BIRD_SHOP } from "../constants";

export interface CartDiscount {
    type: string;
    key: string;
    title: string;
    description: string | null;
    value: string;
    created_at: string;
    value_type: string;
    allocation_method: string;
    target_selection: string;
    target_type: string;
    total_allocated_amount: number;
}

export interface CartData {
    attributes: Record<string, any>;
    cart_level_discount_applications: CartDiscount[];
    discount_codes: any[];
    item_count: number;
    items_subtotal_price: number;
    note: string;
    items: CartLineItem[];
    original_total_price: number;
    requires_shipping: boolean;
    token: string;
    total_discount: number;
    total_price: number;
    total_weight: number;
}

export interface CartLineItem {
    id: number;
    properties: Record<string, any>;
    quantity: number;
    variant_id: number;
    key: string;
    title: string;
    price: number;
    original_price: number;
    presentment_price: number;
    discounted_price: number;
    line_price: number;
    original_line_price: number;
    total_discount: number;
    discounts: { amount: number; title: string }[];
    sku: string;
    grams: number;
    vendor: string;
    taxable: boolean;
    product_id: number;
    product_has_only_default_variant: boolean;
    gift_card: boolean;
    final_price: number;
    final_line_price: number;
    url: string;
    featured_image: {
        aspect_ratio: number;
        alt: string;
        height: number;
        url: string;
        width: number;
    };
    image: string;
    handle: string;
    requires_shipping: boolean;
    product_type: string;
    product_title: string;
    product_description: string;
    variant_title: string;
    variant_options: string[];
    options_with_values: {
        name: string;
        value: string;
    }[];
    line_level_discount_allocations: any[];
    line_level_total_discount: number;
    quantity_rule: {
        min: number;
        max: number | null;
        increment: number;
    };
    has_components: boolean;
    collections?: string[];
    selling_plan_allocation?: {
        selling_plan: {
            name: string;
            recurring_deliveries: boolean;
        };
    };
}

export interface SingleRewardTier {
    type: "product" | "discount" | "free_shipping";
    description: string;
    count: number;
    text_before_tier_completion: string;
    show_tier_completion_text: boolean;
    text_after_tier_completion: string;
    free_products: ProductObj[];
    discount_value: number;
}

export interface DiscountCombinationsInterface {
    reward_free_gift_order_discounts: boolean;
    reward_free_gift_product_discounts: boolean;
    reward_free_gift_shipping_discounts: boolean;
    reward_order_order_discounts: boolean;
    reward_order_product_discounts: boolean;
    reward_order_shipping_discounts: boolean;
    upsell_order_discounts: boolean;
    upsell_product_discounts: boolean;
    upsell_shipping_discounts: boolean;
}
export interface RewardInterface {
    status: boolean;
    show_reward_on_empty_cart: boolean;
    enable_automatic_currency_conversion: boolean;
    text_after_reward_completion: any;
    reward_basis: "cart_total" | "item_count";
    tiers: SingleRewardTier[];
    market_tiers?: { [marketId: string]: SingleRewardTier[] };
    auto_add_free_gifts: boolean;
}

export interface TriggeredRewardsInterface {
    status: boolean;
    tiers: SingleTriggeredRewardTier[];
}

export interface SingleTriggeredRewardTier {
    trigger_type: "product" | "collection";
    products: ProductObj[]; // these are trigger products
    collections: { id: string; image: string; title: string }[];
    free_gifts: ProductObj[];
}

export interface MetalBirdInterface {
    status: boolean;
    free_gifts: ProductObj[];
    product_selection_type: "products" | "collections";
    products: ProductObj[]; // these are trigger products
    collections: { id: string; image: string; title: string }[]; // these are trigger collections
    qualifier_type: "min_quantity" | "min_price";
    count: number;
    unreached_title: string;
    reached_title: string;
    text_color: string;
    text_background_color: string;
    card_background_color: string;
    outline_btn_text_color: string;
    outline_btn_bg_color: string;
    inline_btn_text_color: string;
    inline_btn_bg_color: string;
}

export interface RewardDesignInterface {
    bar_background_color: string;
    bar_foreground_color: string;
    bar_foreground_gradient_status: boolean;
    bar_foreground_gradient_direction: number;
    bar_foreground_gradient_color: string;
    free_gift_bg_color: string;
    free_gift_button_bg_color: string;
    free_gift_button_text_color: string;
    reward_free_gift_text_color: string;
    reward_free_gift_product_title_color: string;
    progress_message_text_color: string;
    free_gift_free_label_color: string;
}

export interface UpsellsInterface {
    upsell_products_type: "all" | "manual";
    recommendation_algorithm: ProductRecommendationType;
    exclude_ai_recommendation_products: number[];
    show_if_item_in_cart: boolean;
    limit_upsells_in_cart: boolean;
    max_upsell_items: number | null;
    upsell_title: string;
    additional_text: string;
    upsell_position: string;
    upsell_layout: UpsellsLayoutType;
    upsell_layout_dir: "vertical" | "horizontal";
    is_upsell_sticky: boolean;
    upsell_status: boolean;
    upsell_offers: {
        upsell_offer_type: "all" | "manual" | "";
        trigger_products: ProductObj[];
        upsell_products: ProductObj[];
        discount_value: string;
        discount_type: string;
        apply_discount_on_single_quantity: boolean;
    }[];
    upsell_triggered_product_required: boolean;
    limit_visible_upsells_in_cart: boolean;
    visible_upsell_limit: number;
}

export interface UpsellDesignInterface {
    upsell_title_color: string;
    upsell_bg_color: string;
    upsell_title_size: number;
    upsell_title_style: string;
    upsell_price_size: number;
    upsell_price_style: FontStylesTypes;
    upsell_compared_price_size: number;
    upsell_compared_price_style: FontStylesTypes;
    upsell_discount_size: number;
    upsell_button_bg_color: string;
    upsell_button_text_color: string;
    upsell_button_border_color: string;
    upsell_button_hover_border_color: string;
    swatches_select_color: string;
    upsell_button_bg_hover_color: string;
    upsell_product_bg_color: string;
    upsell_product_border_color: string;
    upsell_product_text_color: string;
    upsell_product_title_size: number;
    upsell_product_title_style: FontStylesTypes;
    upsell_product_discount_text_color: string;
    upsell_discount_style: FontStylesTypes;
    upsell_button_text_hover_color: string;
    upsell_button_radius: number;
    upsell_card_radius: number;
}

export interface AnnouncementInterface {
    announcement_message_1: string;
    announcement_message_2: string;
    announcement_status: boolean;
    announcement_hight: HeaderHeightTypes;
    countdown_time_hh: number;
    countdown_time_mm: number;
}

export interface AnnouncementDesignInterface {
    message_text_color: string;
    message_text_size: number;
    message_bg_color: string;
    countdown_timer_color: string;
    countdown_timer_size: number;
    message_text_style: FontStylesTypes;
}

export interface DiscountInterface {
    status: boolean;
    title: string;
    placeholder: string;
    cta_text: string;
    label_text: string;
}

export interface DiscountDesignInterface {
    title_color: string;
    icon_color: string;
    cta_bg_color: string;
    cta_bg_hover_color: string;
    cta_text_color: string;
    cta_text_hover_color: string;
    code_bg_color: string;
    code_text_color: string;
}

export interface AdditionalNoteInterface {
    status: boolean;
    title: string;
    placeholder: string;
    cta_text: string;
    Additional_notes_title_text: string;
}

export interface AdditionalNoteDesignInterface {
    title_color: string;
    icon_color: string;
    cta_bg_color: string;
    cta_bg_hover_color: string;
    cta_text_color: string;
    cta_text_hover_color: string;
}

interface custom_field_option {
    label: string;
    value: string;
}

export interface custom_field {
    field_type: "input" | "textarea" | "radio"; // input, textarea or radio buttons
    field_title: string; // field label
    field_placeholder?: string;
    is_required: boolean;
    options?: custom_field_option[];
}

export interface AdditionalProductsInterface {
    status: boolean;
    product_status: boolean;
    product_type: AdditionalProductType;
    shipping_protection_title: string;
    additional_product_shipping_gift_checkbox: boolean;
    additional_product_addon_checkbox: boolean;
    additional_addon_title: string;
    additional_addon_subtext: string;
    addon_selector_label: string;
    addon_modal_title: string;
    additional_addon_products?: ProductObj[];
    custom_fields_checkbox: boolean;
    custom_fields: custom_field[];
    shipping_protection_subtext: string;
    shipping_protection_price: number;
    shipping_protection_incremental_value: number;
    shipping_protection_images: any[];
    shipping_protection_price_type: "fixed" | "incremental";
    shipping_protection_id?: string; //new

    gift_wrapping_title: string;
    gift_wrapping_subtext: string;
    gift_wrapping_price: number;
    gift_wrapping_incremental_value: number;
    gift_wrapping_images: any[];
    gift_wrapping_price_type: "fixed" | "incremental";
    gift_wrapping_id?: string; //new
}

export interface AdditionalProductsDesignInterface {
    additional_product_title_size: number;
    additional_product_description_size: number;
    additional_product_description_style: FontStylesTypes;
    additional_product_card_bg: string;
    additional_product_title_color: string;
    additional_product_description_color: string;
    additional_product_border_color: string;
    additional_product_border_gradient_status: boolean;
    additional_product_border_gradient_color: string;
    additional_product_border_gradient_direction: number;

    addon_modal_title_size: number;
    addon_modal_background_color: string;
    addon_modal_title_color: string;
    addon_modal_primary_button_bg: string;
    addon_modal_primary_button_text_color: string;
    addon_modal_secondary_button_text_color: string;
}

export interface AdditionalMessageInterface {
    status: boolean;
    additional_msg_text: string;
    additional_msg_bg: string;
    additional_msg_color: string;
    additional_msg_text_size: number;
    additional_msg_text_style: FontStylesTypes;
}

export interface CartDesignInterface {
    cart_font_family: string;
    cart_bg_color: string;
    cart_width: CartWidthTypes;
    cart_primary_color: string;
    cart_lighten_primary_color: string;
    cart_gradient_color: string;
    header_bg_color: string;
    close_button_color: string;
    header_title_underline_color: string;
    header_title_underline_color_gradient_status: boolean;
    header_title_underline_color_gradient_color: string;
    header_title_underline_color_gradient_direction: number;
    header_title_color: string;
    header_quantity_color: string;
    header_height: HeaderHeightTypes;
    cartline_card_bg_color: string;
    cartline_card_text_color: string;
    cartline_card_save_text_color: string;
    cartline_card_qty_color: string;
    cartline_card_discount_bg_color: string;
    cartline_card_discount_text_color: string;
    cartline_card_title_size: number;
    cartline_card_title_style: FontStylesTypes;
    cartline_card_properties_size: number;
    cartline_card_properties_style: FontStylesTypes;
    cartline_card_price_size: number;
    cartline_card_price_style: FontStylesTypes;
    cartline_card_compared_price_size: number;
    cartline_card_compared_price_style: FontStylesTypes;
    cartline_card_save_text_size: number;
    cartline_card_save_text_style: FontStylesTypes;
    footer_text_color: string;
    footer_text_size: number;
    footer_text_style: FontStylesTypes;
    footer_compare_at_color: string;
    footer_compare_at_size: number;
    footer_compare_at_style: FontStylesTypes;
    footer_secondary_text_color: string;
    footer_secondary_text_size: number;
    footer_secondary_text_style: FontStylesTypes;
    footer_bg_color: string;
    footer_button_radius: number;
    footer_button_cta_text_size: number;
    footer_custom_shipping_button_color?: string;
    footer_button_cta_text_style: FontStylesTypes;
    footer_button_bg_color: string;
    footer_button_border_color: string;
    footer_button_hover_border_color: string;
    footer_button_text_color: string;
    footer_button_bg_hover_color: string;
    footer_button_text_hover_color: string;
    footer_discount_tag_bg_color: string;
    footer_discount_tag_text_color: string;
}

export interface CartSettingsInterface {
    // cart behavior
    redirect_to_checkout: boolean;
    show_text_below_add_to_cart: boolean;
    text_below_add_to_cart: string;
    show_cart_cta: boolean;
    cart_position: "left" | "right";
    show_continue_shopping_cta: boolean;
    open_cart_on_product_add: boolean;
    show_swatches: boolean;
    //product/pricing display
    show_compare_at: boolean;
    sticky_footer: boolean;
    show_total_saved: boolean;
    custom_html_position: CustomHtmlPosTypes;
    custom_html_text: string;
    custom_css_text: string;
    show_discounts_form_compare_at: boolean;
    hide_product_variant_label: boolean;
    show_product_properties: boolean;
    show_compare_at_price: boolean;
}

export interface BadgeType {
    selected: boolean;
    originalSrc: string;
    alt?: string;
    deletable?: boolean;
}
export interface TrustBadgesInterface {
    status: boolean;
    badge_size: string;
    badges: BadgeType[];
}

export interface TermsAndConditionsInterface {
    status: boolean;
    text: string;
    required_text: string;
}

export interface TermsAndConditionsDesignInterface {
    text_color: string;
    link_color: string;
}

export interface CartConfigInterface {
    show_kefi_branding: boolean;
    cart_status: boolean;
    is_paused: boolean;
    cart_id: string;
    cart_title: string;
    selected_drawer_template: CartDrawerTemplatesTypes;
    selected_drawer_footer_layout: CartDrawerFooterTypes;
    selected_drawer_color: string;
    checkout_cta_text: string;
    checkout_icon_url: string;
    upsell_cta_icon_url: string;
    empty_cart_text: string;
    continue_shopping_cta_text: string;
    applied_discount_text: string;
    add_upsell_text: string;
    reward_free_gift_text: string;
    estimated_total_text: string;
    show_underline_below_cart_text: boolean;
    order_discount_message_text: string;
    bill_summary_text: string;
    item_total_text: string;
    shipping_text: string;
    shipping_message_text: string;
    cart_discounts_text: string;
    grand_total_text: string;
    cart_item_text: string;
    cart_items_text: string;
    view_cart_cta_text: string;
    continue_shop_cta_text: string;
    saving_label_text: string;
    free_gift_cta: string;
    free_gift_free_label: string;
    cart_features_positions: { label: string; key: string }[];
    discount_combinations: DiscountCombinationsInterface;
    reward: RewardInterface;
    reward_design: RewardDesignInterface;
    triggered_rewards: TriggeredRewardsInterface;
    upsell: UpsellsInterface;
    upsell_design: UpsellDesignInterface;
    announcement: AnnouncementInterface;
    announcement_design: AnnouncementDesignInterface;
    discount: DiscountInterface;
    discount_design: DiscountDesignInterface;
    additional_note: AdditionalNoteInterface;
    additional_note_design: AdditionalNoteDesignInterface;
    additional_product: AdditionalProductsInterface;
    additional_product_design: AdditionalProductsDesignInterface;
    additional_message: AdditionalMessageInterface;
    cart_design: CartDesignInterface;
    cart_settings: CartSettingsInterface;
    cart_custom_text?: string[];
    trust_badges: TrustBadgesInterface;
    metal_bird_config?: MetalBirdInterface;
    terms_and_conditions: TermsAndConditionsInterface;
    terms_and_conditions_design: TermsAndConditionsDesignInterface;
}

export const initialCartConfig: CartConfigInterface = {
    show_kefi_branding: true,
    cart_status: false,
    is_paused: false,
    cart_id: "",
    selected_drawer_template: AvailableCartDrawerTemplates.BASIC_LAYOUT,
    selected_drawer_footer_layout: AvailableCartFooterLayouts.HORIZONTAL,
    selected_drawer_color: '{"alpha":1,"brightness":0,"hex":"#000000","hue":0,"saturation":0}',
    cart_title: "Cart",
    checkout_cta_text: "{icon} Checkout",
    checkout_icon_url: "",
    upsell_cta_icon_url: "",
    empty_cart_text: "Cart is Empty!",
    continue_shopping_cta_text: "Continue Shopping",
    applied_discount_text: "Applied Discount",
    view_cart_cta_text: "View Cart",
    continue_shop_cta_text: "Continue Shopping",
    saving_label_text: "Saving",
    add_upsell_text: "Add",
    reward_free_gift_text: "🎁 Grab your free gift !",
    free_gift_cta: "Add",
    free_gift_free_label: "FREE",
    estimated_total_text: "Estimated total",
    show_underline_below_cart_text: true,
    order_discount_message_text: "You saved {amount_saved} with the {offer} on your order.",
    bill_summary_text: "Bill Summary",
    item_total_text: "Item Total",
    shipping_text: "Shipping",
    shipping_message_text: "To be calculated at checkout",
    cart_discounts_text: "Cart Discounts",
    grand_total_text: "Grand Total",
    cart_item_text: "Item",
    cart_items_text: "Items",
    cart_features_positions: [
        {
            label: "Announcement",
            key: "announcement",
        },
        {
            label: "Rewards",
            key: "reward",
        },
        ...(IS_METAL_BIRD_SHOP
            ? [
                  {
                      label: "Free Gift",
                      key: "free_gifts",
                  },
              ]
            : []),
        {
            label: "Triggered Rewards",
            key: "triggered_rewards",
        },
        {
            label: "Products",
            key: "products",
        },
        {
            label: "Upsells",
            key: "upsell",
        },
    ],
    discount_combinations: {
        reward_free_gift_order_discounts: true,
        reward_free_gift_product_discounts: true,
        reward_free_gift_shipping_discounts: true,
        reward_order_order_discounts: true,
        reward_order_product_discounts: true,
        reward_order_shipping_discounts: true,
        upsell_order_discounts: true,
        upsell_product_discounts: true,
        upsell_shipping_discounts: true,
    },
    triggered_rewards: {
        status: false,
        tiers: [],
    },
    reward: {
        show_reward_on_empty_cart: true,
        status: true,
        text_after_reward_completion: "<p>Congrats, you achieved the goal.</p>",
        reward_basis: "cart_total",
        tiers: [
            {
                type: "discount",
                description: "10% discount",
                count: 1000,
                discount_value: 10,
                free_products: [],
                text_before_tier_completion: "<p>You're {amount} away from getting 10% discount</p>",
                show_tier_completion_text: false,
                text_after_tier_completion: "<p>Congrats! You've received a 10% discount.</p>",
            },
        ],
        market_tiers: {},
        enable_automatic_currency_conversion: true,
        auto_add_free_gifts: false,
    },
    reward_design: {
        bar_background_color: "#D9D9D9",
        bar_foreground_color: "#000000",
        bar_foreground_gradient_status: false,
        bar_foreground_gradient_direction: 90,
        bar_foreground_gradient_color: "#FFFFFF",
        free_gift_bg_color: "#D9D9D9",
        free_gift_button_bg_color: "#030303",
        free_gift_button_text_color: "#ffffff",
        reward_free_gift_text_color: "#030303",
        free_gift_free_label_color: "#008000",
        reward_free_gift_product_title_color: "#030303",
        progress_message_text_color: "#030303",
    },
    announcement: {
        announcement_message_1: "Your products are reserved for {timer} minutes!",
        announcement_message_2: "message",
        announcement_status: true,
        announcement_hight: "header_medium",
        countdown_time_hh: 0,
        countdown_time_mm: 1,
    },
    announcement_design: {
        message_text_color: "#000000",
        message_text_size: 13,
        message_bg_color: "#D9D9D9",
        countdown_timer_color: "#000000",
        countdown_timer_size: 13,
        message_text_style: "450 normal",
    },
    upsell: {
        upsell_products_type: "all",
        recommendation_algorithm: ProductRecommendationIntent.RELATED as ProductRecommendationType,
        exclude_ai_recommendation_products: [],
        show_if_item_in_cart: true,
        limit_upsells_in_cart: false,
        max_upsell_items: 2,
        upsell_title: "<p><strong>Special Offer</strong></p>",
        additional_text: "Limited time deal!",
        upsell_position: "top",
        upsell_layout: "carousel",
        upsell_layout_dir: "horizontal",
        is_upsell_sticky: false,
        upsell_status: true,
        upsell_offers: [],
        upsell_triggered_product_required: false,
        limit_visible_upsells_in_cart: false,
        visible_upsell_limit: 2,
    },
    upsell_design: {
        upsell_title_color: "#000000",
        upsell_bg_color: "#FFFFFF",
        upsell_title_style: "650 normal",
        upsell_title_size: 14,
        upsell_price_size: 14,
        upsell_price_style: "450 normal",
        upsell_compared_price_size: 11,
        upsell_compared_price_style: "450 normal",
        upsell_discount_size: 10,
        upsell_product_bg_color: "#D9D9D9",
        upsell_product_border_color: "#dedede",
        upsell_product_text_color: "#303030",
        upsell_product_title_size: 14,
        upsell_product_title_style: "650 normal",
        upsell_product_discount_text_color: "#2ea818",
        upsell_discount_style: "450 normal",
        upsell_button_bg_color: "#000000",
        upsell_button_bg_hover_color: "#D9D9D9",
        upsell_button_text_color: "#FFFFFF",
        upsell_button_border_color: "#000000",
        upsell_button_hover_border_color: "#000000",
        upsell_button_text_hover_color: "#000000",
        upsell_button_radius: 8,
        upsell_card_radius: 8,
        swatches_select_color: "#000000",
    },
    discount: {
        status: false,
        title: "<p><strong>Discount Coupon</strong></p>",
        placeholder: "Discount code",
        cta_text: "Apply",
        label_text: "Apply Discount Code",
    },
    discount_design: {
        title_color: "#000000",
        icon_color: "#616161",
        cta_bg_color: "#000000",
        cta_bg_hover_color: "#f1f1f1",
        cta_text_color: "#f1f1f1",
        cta_text_hover_color: "#000000",
        code_bg_color: "#D9D9D9",
        code_text_color: "#000000",
    },
    additional_note: {
        status: false,
        title: "<p><strong>Additional Note</strong></p>",
        placeholder: "Add a note to your order",
        cta_text: "Save",
        Additional_notes_title_text: "Additional Notes",
    },
    additional_note_design: {
        title_color: "#000000",
        icon_color: "#616161",
        cta_bg_color: "#000000",
        cta_bg_hover_color: "#f1f1f1",
        cta_text_color: "#f1f1f1",
        cta_text_hover_color: "#000000",
    },
    additional_product: {
        status: false,
        product_status: false,
        product_type: "shipping_protection",
        shipping_protection_title: "<p><strong>Shipping Protection</strong></p>",
        additional_product_addon_checkbox: false,
        additional_product_shipping_gift_checkbox: true,
        additional_addon_title: "<p><strong>Add gift wrapping & greeting card for {productPrice}</strong></p>",
        additional_addon_subtext: "",
        addon_selector_label: "Greeting Card",
        addon_modal_title: "Gift option information",
        additional_addon_products: [],
        custom_fields_checkbox: true,
        custom_fields: [
            {
                field_type: "textarea",
                field_title: "Message - We will print this inside the Greeting Card or on the back of the postcard.",
                field_placeholder:
                    "Message - We will print this inside the Greeting Card or on the back of the postcard.",
                is_required: false,
                options: [],
            },
        ],
        shipping_protection_subtext: "Get this product at {price}",
        shipping_protection_price: 3,
        shipping_protection_incremental_value: 0.5,
        shipping_protection_images: [
            {
                originalSource: "https://storage.googleapis.com/scalenut/e997c840-823b-49fa-b618-a4300821acb4.png",
                alt: "shipping_protection.png",
                mediaContentType: "IMAGE",
            },
        ],
        shipping_protection_price_type: "fixed",
        // for gift wrapping;
        gift_wrapping_title: "<p><strong>Gift Wrapping</strong></p>",
        gift_wrapping_subtext: "Get this product at {price}",
        gift_wrapping_price: 1,
        gift_wrapping_incremental_value: 0.5,
        gift_wrapping_images: [
            {
                originalSource: "https://storage.googleapis.com/scalenut/f3211673-2bad-476f-b231-7ce30be3b645.png",
                alt: "gift_wrapping.png",
                mediaContentType: "IMAGE",
            },
        ],
        gift_wrapping_price_type: "fixed",
    },
    additional_product_design: {
        additional_product_title_size: 12,
        additional_product_description_size: 10,
        additional_product_description_style: "450 normal",
        additional_product_card_bg: "#FFFFFF",
        additional_product_title_color: "#303030",
        additional_product_description_color: "#8a8a8a",
        additional_product_border_color: "#000000",
        additional_product_border_gradient_status: false,
        additional_product_border_gradient_color: "#4D4D4D",
        additional_product_border_gradient_direction: 90,

        addon_modal_title_size: 14,
        addon_modal_background_color: "#edeae6",
        addon_modal_title_color: "#2C2C2C",
        addon_modal_primary_button_bg: "#B98B37",
        addon_modal_primary_button_text_color: "#FFFFFF",
        addon_modal_secondary_button_text_color: "#B98B37",
    },
    additional_message: {
        status: false,
        additional_msg_text: "Amazing choice",
        additional_msg_bg: "#FFFFFF",
        additional_msg_color: "#303030",
        additional_msg_text_size: 13,
        additional_msg_text_style: "450 normal",
    },
    cart_design: {
        cart_font_family: "Custom Store Font",
        cart_bg_color: "#ffffff",
        cart_width: "cart_width_medium",
        cart_primary_color: "#303030",
        cart_lighten_primary_color: "#303030",
        cart_gradient_color: "#000000",
        // Header
        header_bg_color: "#000000",
        header_title_color: "#FFFFFF",
        header_quantity_color: "#FFFFFF",
        header_height: "header_medium",
        close_button_color: "#FFFFFF",
        header_title_underline_color: "#000000",
        header_title_underline_color_gradient_status: false,
        header_title_underline_color_gradient_color: "#FFFFFF",
        header_title_underline_color_gradient_direction: 90,
        // CartLine Card
        cartline_card_bg_color: "#ffffff",
        cartline_card_text_color: "#303030",
        cartline_card_save_text_color: "#2ea818",
        cartline_card_qty_color: "#303030",
        cartline_card_discount_bg_color: "#000000",
        cartline_card_discount_text_color: "#FFFFFF",
        cartline_card_title_size: 14,
        cartline_card_title_style: "650 normal",
        cartline_card_properties_size: 10,
        cartline_card_properties_style: "450 normal",
        cartline_card_price_size: 14,
        cartline_card_price_style: "450 normal",
        cartline_card_compared_price_size: 11,
        cartline_card_compared_price_style: "450 normal",
        cartline_card_save_text_size: 10,
        cartline_card_save_text_style: "450 normal",
        // Footer
        footer_text_color: "#303030",
        footer_text_size: 14,
        footer_text_style: "450 normal",
        footer_compare_at_color: "#b5b5b5",
        footer_compare_at_size: 12,
        footer_compare_at_style: "450 normal",
        footer_secondary_text_color: "#A1A8B0",
        footer_secondary_text_size: 12,
        footer_secondary_text_style: "450 normal",
        footer_bg_color: "#FFFFFF",
        footer_button_bg_color: "#000000",
        footer_button_border_color: "#000000",
        footer_button_hover_border_color: "#000000",
        footer_button_bg_hover_color: "#D9D9D9",
        footer_button_radius: 8,
        footer_button_cta_text_size: 16,
        footer_custom_shipping_button_color: "#c7a046",
        footer_button_cta_text_style: "450 normal",
        footer_button_text_color: "#FFFFFF",
        footer_button_text_hover_color: "#000000",
        footer_discount_tag_bg_color: "#000000",
        footer_discount_tag_text_color: "#ffffff",
    },
    cart_settings: {
        redirect_to_checkout: false,
        show_text_below_add_to_cart: false,
        text_below_add_to_cart: "*All orders are secured*",
        show_cart_cta: false,
        cart_position: "right",
        show_continue_shopping_cta: false,
        open_cart_on_product_add: true,
        show_swatches: false,

        //product/pricing display
        show_compare_at: false,
        sticky_footer: true,
        show_total_saved: true,
        custom_html_position: "below_checkout",
        custom_html_text: ``,
        custom_css_text: "",
        show_discounts_form_compare_at: false,
        hide_product_variant_label: false,
        show_product_properties: true,
        show_compare_at_price: false,
    },
    trust_badges: {
        status: false,
        badge_size: "45",
        badges: [
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/b3cfe237-124b-46c3-97a7-5b5de03c6b9b.svg",
                alt: "Affirm",
                selected: false,
                deletable: false,
            },
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/868b1cfb-a47a-4933-a105-58f26b75799d.svg",
                alt: "AliPay",
                selected: true,
                deletable: false,
            },
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/99af3347-ae0f-49f9-bbca-cb9edaebd4e0.svg",
                alt: "AmazonPay",
                selected: false,
                deletable: false,
            },
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/1273aac5-992b-4d8e-a2e6-f55f171d26f8.svg",
                alt: "Amex",
                selected: false,
                deletable: false,
            },
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/ca6e9b1d-cb94-4def-9333-50c6984111de.svg",
                alt: "ApplePay",
                selected: false,
                deletable: false,
            },
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/2cd55fda-d130-4353-9fe9-c6edfb5c270b.svg",
                alt: "BitCoin",
                selected: false,
                deletable: false,
            },
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/01bd0670-c387-44ce-a0ea-e75e05dd80ee.svg",
                alt: "BitCoinCash",
                selected: false,
                deletable: false,
            },
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/22532b12-df3b-44f6-b83a-1420311d6e5c.svg",
                alt: "BitPay",
                selected: false,
                deletable: false,
            },
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/a97b1e19-1b1c-4018-bebf-44962a7db4b6.svg",
                alt: "Citadele",
                selected: false,
                deletable: false,
            },
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/e27f0294-d8d4-4e25-a438-d4d2e018bc8b.svg",
                alt: "DinnersClub",
                selected: false,
                deletable: false,
            },
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/ed0f94e9-475d-455a-b32c-e0bceb88f63c.svg",
                alt: "Discover",
                selected: false,
                deletable: false,
            },
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/61dc0a11-d48c-4e96-a6b3-0ac2cc9873f4.svg",
                alt: "FacebookPay",
                selected: false,
                deletable: false,
            },
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/c7ad5669-30a7-4bb8-aa59-fd551639fcf5.svg",
                alt: "GooglePay",
                selected: false,
                deletable: false,
            },
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/97582f58-78b5-47f0-aa09-3b46ba1de95e.svg",
                alt: "JCBS",
                selected: false,
                deletable: false,
            },
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/71693832-6e7b-4d4d-b797-3c79b7bb87cf.svg",
                alt: "Mastercard",
                selected: false,
                deletable: false,
            },
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/c25a0de8-2356-4ca8-9b3e-fde3ff6ee02f.svg",
                alt: "PayPal",
                selected: true,
                deletable: false,
            },
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/7f759ecb-576a-44ce-921a-cbe7c986e1e9.svg",
                alt: "Stripe",
                selected: true,
                deletable: false,
            },
            {
                originalSrc:
                    "https://storage.scalenut.com/prod/user-upload-images/0780d9c3-9cc5-4c53-9d6c-0762fb64f015.svg",
                alt: "Visa",
                selected: true,
                deletable: false,
            },
        ],
    },
    metal_bird_config: {
        status: false,
        free_gifts: [],
        product_selection_type: "products",
        products: [],
        collections: [],
        qualifier_type: "min_quantity",
        count: 2,
        unreached_title: "GRAB 2 REGULAR BIRDS AND GET FREE MINI",
        reached_title: "CHOOSE A FREE MINI",
        text_color: "#ffffff",
        card_background_color: "#f1efec",
        text_background_color: "#c30631",
        inline_btn_bg_color: "#ffffff",
        inline_btn_text_color: "#c8c1a7",
        outline_btn_bg_color: "#c8c1a7",
        outline_btn_text_color: "#ffffff",
    },
    terms_and_conditions: {
        status: false,
        text: "I have read and agree to the <a target='_blank' href='https://www.example.com'>terms and conditions</a>",
        required_text: "Please agree to the terms and conditions",
    },
    terms_and_conditions_design: {
        text_color: "#000000",
        link_color: "#2C6ECB",
    },
};
